import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import Navbar from '../components/Navbar';
import PromoSlot from '../components/PromoBanner';
import { useCart } from '../contexts/CartContext';
import { describeLine } from '../lib/cartLine';
import PriceTag from '../components/PriceTag';
import { Trash2, ShoppingCart, ArrowRight, Package, Bookmark, AlertTriangle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function LineImage({ item }) {
  return (
    <div className="w-20 h-20 rounded-xl overflow-hidden bg-ink/[0.04] flex-shrink-0">
      {item.image ? <img src={item.image} alt={item.title} className="w-full h-full object-cover" /> : <Package className="w-8 h-8 text-ink/30 m-auto mt-6" />}
    </div>
  );
}

function Price({ item }) {
  if (!item.available) return null;
  return (
    <>
      <PriceTag now={item.price} was={item.original_price > item.price ? item.original_price : null} className="text-sm" />
      {item.sale_name && <p className="text-honey-600/70 text-xs">{item.sale_name}</p>}
    </>
  );
}

export default function Cart() {
  const { cart, updateQuantity, clearCart, saveForLater, moveToCart, removeSaved } = useCart();
  const navigate = useNavigate();
  const { items, subtotal } = cart;
  const saved = cart.saved || [];
  const [quote, setQuote] = useState(null);

  useEffect(() => {
    if (!items.length) { setQuote(null); return; }
    axios.get(`${API}/checkout/quote`, { withCredentials: true }).then(r => setQuote(r.data)).catch(() => setQuote(null));
  }, [cart]); // eslint-disable-line react-hooks/exhaustive-deps

  const tax = quote?.tax ?? 0;
  const shipping = quote?.shipping ?? 0;
  const total = quote?.total ?? subtotal;
  const freeAt = quote?.free_shipping_threshold;
  const unavailable = items.some(i => i.available === false);

  const savedList = saved.length > 0 && (
    <div className="mt-10">
      <h2 className="text-lg font-bold text-ink mb-4 flex items-center gap-2"><Bookmark className="w-4 h-4 text-honey-700" /> Saved for later ({saved.length})</h2>
      <div className="space-y-3">
        {saved.map(item => (
          <div key={item.line_id} className="glass-card rounded-2xl p-4 flex items-center gap-4" data-testid={`saved-item-${item.line_id}`}>
            <LineImage item={item} />
            <div className="flex-1 min-w-0">
              <Link to={`/products/${item.product_id}`} className="text-ink font-semibold text-sm truncate block hover:text-honey-700">{item.title}</Link>
              {describeLine(item) && <p className="text-ink/55 text-xs truncate">{describeLine(item)}</p>}
              {item.available ? <Price item={item} /> : <p className="text-red-600 text-xs">{item.unavailable_reason}</p>}
            </div>
            {item.available && <button onClick={() => moveToCart(item.line_id)} className="btn-secondary text-sm py-2 px-4">Move to cart</button>}
            <button onClick={() => removeSaved(item.line_id)} className="text-red-600/60 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
      </div>
    </div>
  );

  if (items.length === 0) return (
    <div className="store-page relative">
      <Navbar />
      <div className="relative z-10 max-w-5xl mx-auto px-6 pt-12 pb-20">
        <div className="flex flex-col items-center justify-center">
          <div className="w-20 h-20 bg-ink/[0.04] rounded-full flex items-center justify-center mb-6">
            <ShoppingCart className="w-10 h-10 text-ink/30" />
          </div>
          <h2 className="text-2xl font-black text-ink mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>Your cart is empty</h2>
          <p className="text-ink/55 mb-8">Add some products to get started</p>
          <Link to="/" className="btn-primary" data-testid="continue-shopping-btn">Continue Shopping</Link>
        </div>
        {savedList}
      </div>
    </div>
  );

  return (
    <div className="store-page relative">
      <Navbar />
      <div className="relative z-10 max-w-5xl mx-auto px-6 pt-8 pb-20">
        <h1 className="text-3xl font-black text-ink mb-8" style={{ fontFamily: 'Outfit, sans-serif' }}>
          Shopping Cart <span className="text-honey-700">({items.length})</span>
        </h1>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-4">
            {items.map((item, i) => (
              <motion.div key={item.line_id} initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.05 }}
                className={`glass-card rounded-2xl p-4 flex flex-wrap items-center gap-4 ${item.available === false ? 'opacity-60' : ''}`}
                data-testid={`cart-item-${item.line_id}`}>
                <LineImage item={item} />
                <div className="flex-1 min-w-[140px]">
                  <Link to={`/products/${item.product_id}`} className="text-ink font-semibold text-sm truncate block hover:text-honey-700">{item.title}</Link>
                  {describeLine(item) && <p className="text-ink/55 text-xs truncate">{describeLine(item)}</p>}
                  {item.is_bundle && <p className="text-ink/45 text-xs truncate">Includes {item.bundle_items.map(b => `${b.quantity > 1 ? b.quantity + '× ' : ''}${b.title}`).join(', ')}</p>}
                  {item.available === false ? <p className="text-red-600 text-xs flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> {item.unavailable_reason}</p> : <Price item={item} />}
                  <button onClick={() => saveForLater(item.line_id)} className="text-ink/55 hover:text-honey-700 text-xs mt-1 flex items-center gap-1">
                    <Bookmark className="w-3 h-3" /> Save for later
                  </button>
                </div>
                {item.available !== false && (
                  <div className="flex items-center gap-2 bg-ink/[0.04] rounded-xl overflow-hidden">
                    <button onClick={() => updateQuantity(item.line_id, item.quantity - 1)} className="px-3 py-2 text-ink hover:bg-ink/[0.07]">-</button>
                    <span className="px-3 py-2 text-ink text-sm font-bold min-w-[2rem] text-center">{item.quantity}</span>
                    <button onClick={() => updateQuantity(item.line_id, item.quantity + 1)} className="px-3 py-2 text-ink hover:bg-ink/[0.07]">+</button>
                  </div>
                )}
                <p className="text-ink font-bold w-20 text-right">{item.available === false ? '—' : `$${item.line_total?.toFixed(2)}`}</p>
                <button onClick={() => updateQuantity(item.line_id, 0)} className="text-red-600/60 hover:text-red-600 ml-2"><Trash2 className="w-4 h-4" /></button>
              </motion.div>
            ))}
            <button onClick={clearCart} className="text-ink/45 hover:text-red-600 text-sm flex items-center gap-2 transition-colors">
              <Trash2 className="w-4 h-4" /> Clear cart
            </button>
            {savedList}
          </div>

          <div className="space-y-4">
            <PromoSlot placement="cart" />
            <div className="glass-card rounded-2xl p-6 h-fit">
              <h3 className="text-lg font-bold text-ink mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>Order Summary</h3>
              <div className="space-y-3 text-sm mb-6">
                <div className="flex justify-between text-ink/70"><span>Subtotal</span><span>${(quote?.subtotal ?? subtotal).toFixed(2)}</span></div>
                {(quote?.discounts || []).filter(d => d.type === 'deal').map(d => (
                  <div key={d.deal_id} className="flex justify-between text-green-600"><span>{d.name}{d.sets > 1 ? ` ×${d.sets}` : ''}</span><span>-${d.amount.toFixed(2)}</span></div>
                ))}
                <div className="flex justify-between text-ink/70"><span>Tax{quote?.tax_rate ? ` (${quote.tax_rate}%)` : ''}</span><span>${tax.toFixed(2)}</span></div>
                <div className="flex justify-between text-ink/70">
                  <span>Shipping</span>
                  <span>{shipping === 0 ? <span className="text-green-600">Free</span> : `$${shipping.toFixed(2)}`}</span>
                </div>
                {shipping > 0 && freeAt != null && subtotal < freeAt && (
                  <p className="text-ink/45 text-xs">Add ${(freeAt - subtotal).toFixed(2)} more for free shipping</p>
                )}
                <div className="border-t border-ink/10 pt-3 flex justify-between text-ink font-bold text-base">
                  <span>Total</span><span className="text-honey-700">${total.toFixed(2)}</span>
                </div>
                <p className="text-ink/45 text-xs">Have a coupon? Add it at checkout.</p>
              </div>
              {unavailable && <p className="text-red-600 text-xs mb-3">Remove or save the unavailable items to check out.</p>}
              <button onClick={() => navigate('/checkout')} disabled={unavailable}
                className="w-full btn-primary flex items-center justify-center gap-2 disabled:opacity-40" data-testid="checkout-btn">
                Proceed to Checkout <ArrowRight className="w-4 h-4" />
              </button>
              <Link to="/" className="block text-center text-ink/55 hover:text-ink text-sm mt-4 transition-colors">Continue Shopping</Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
